/* ============================================================
   Hero Slideshow — hero-slideshow.js
   Vanilla JS, sem dependências, compatível com Shopify 2.0
   ============================================================ */

class HeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this._currentIndex = 0;
    this._total        = 0;
    this._timer        = null;
    this._isAnimating  = false;
    this._delay        = 6000;
    this._autoplay     = true;
    this._pauseHover   = true;
    this._transition   = 'fade';
  }

  connectedCallback() {
    this._init();
  }

  disconnectedCallback() {
    this._clearTimer();
    this._removeListeners();
  }

  _init() {
    this._slides      = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
    this._dots        = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this._prevBtn     = this.querySelector('.hero-slideshow__nav--prev');
    this._nextBtn     = this.querySelector('.hero-slideshow__nav--next');
    this._counter     = this.querySelector('.hero-slideshow__counter-current');
    this._counterBar  = this.querySelector('.hero-slideshow__counter-progress');
    this._progressBar = this.querySelector('.hero-slideshow__progress-fill');

    this._total      = this._slides.length;
    this._delay      = parseInt(this.dataset.delay, 10)  || 6000;
    this._autoplay   = this.dataset.autoplay !== 'false';
    this._transition = this.dataset.transition || 'fade';
    this._pauseHover = this.dataset.pauseOnHover !== 'false';

    if (this._total <= 1) return;

    this._applyContentVars();
    this._bindListeners();
    if (this._autoplay) this._startTimer();
  }

  /* ---- Per-slide CSS vars from inline style data ---- */
  _applyContentVars() {
    this._slides.forEach((slide) => {
      const maxW = slide.dataset.contentMaxWidth || slide.getAttribute('data-content-max-width');
      const ts   = slide.dataset.titleSize || slide.getAttribute('data-title-size');
      const tsm  = slide.dataset.titleSizeMobile || slide.getAttribute('data-title-size-mobile');
      const tw   = slide.dataset.titleWeight || slide.getAttribute('data-title-weight');
      const ss   = slide.dataset.subtitleSize || slide.getAttribute('data-subtitle-size');
      const ip   = slide.dataset.imagePosition || slide.getAttribute('data-image-position');
      const content = slide.querySelector('.hero-slideshow__content');
      const image   = slide.querySelector('.hero-slideshow__image');

      if (maxW && content)   content.style.maxWidth  = maxW + 'px';
      if (ts  && slide)      slide.style.setProperty('--title-size', ts + 'px');
      if (tsm && slide)      slide.style.setProperty('--title-size-mobile', tsm + 'px');
      if (tw  && slide)      slide.style.setProperty('--title-weight', tw);
      if (ss  && slide)      slide.style.setProperty('--subtitle-size', ss + 'px');
      if (ip  && image)      image.style.objectPosition = ip;
    });
  }

  /* ---- Event Listeners ---- */
  _bindListeners() {
    this._onPrev  = () => this._goTo(this._currentIndex - 1);
    this._onNext  = () => this._goTo(this._currentIndex + 1);
    this._onDot   = (e) => {
      const target = parseInt(e.currentTarget.dataset.slideTarget, 10);
      if (!isNaN(target)) this._goTo(target);
    };
    this._onKeyboard = (e) => {
      if (e.key === 'ArrowLeft')  this._goTo(this._currentIndex - 1);
      if (e.key === 'ArrowRight') this._goTo(this._currentIndex + 1);
    };

    if (this._prevBtn) this._prevBtn.addEventListener('click', this._onPrev);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._onNext);
    this._dots.forEach(dot => dot.addEventListener('click', this._onDot));
    this.addEventListener('keydown', this._onKeyboard);

    if (this._pauseHover && this._autoplay) {
      this._onMouseEnter = () => this._clearTimer();
      this._onMouseLeave = () => this._startTimer();
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
    }

    /* Touch / Swipe */
    this._touchStartX = null;
    this._onTouchStart = (e) => { this._touchStartX = e.touches[0].clientX; };
    this._onTouchEnd   = (e) => {
      if (this._touchStartX === null) return;
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      if (Math.abs(dx) > 40) this._goTo(this._currentIndex + (dx < 0 ? 1 : -1));
      this._touchStartX = null;
    };
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.addEventListener('touchend',   this._onTouchEnd,   { passive: true });
  }

  _removeListeners() {
    if (this._prevBtn) this._prevBtn.removeEventListener('click', this._onPrev);
    if (this._nextBtn) this._nextBtn.removeEventListener('click', this._onNext);
    this._dots.forEach(dot => dot.removeEventListener('click', this._onDot));
    this.removeEventListener('keydown',      this._onKeyboard);
    this.removeEventListener('mouseenter',   this._onMouseEnter);
    this.removeEventListener('mouseleave',   this._onMouseLeave);
    this.removeEventListener('touchstart',   this._onTouchStart);
    this.removeEventListener('touchend',     this._onTouchEnd);
  }

  /* ---- Navigation ---- */
  _goTo(index) {
    if (this._isAnimating) return;

    /* Wrap */
    const next = ((index % this._total) + this._total) % this._total;
    if (next === this._currentIndex) return;

    this._isAnimating = true;
    const prev  = this._currentIndex;
    const speed = parseInt(getComputedStyle(this).getPropertyValue('--slide-transition-speed'), 10) || 700;

    /* Transition classes */
    if (this._transition === 'slide') {
      const dir = next > prev ? 1 : -1;
      this._slides[prev].style.transform  = `translateX(${-100 * dir}%)`;
      this._slides[next].style.transform  = `translateX(${100 * dir}%)`;
      this._slides[next].classList.add('is-active');
      requestAnimationFrame(() => {
        this._slides[next].style.transform = 'translateX(0)';
        this._slides[next].style.opacity   = '1';
      });
    } else {
      this._slides[next].classList.add('is-active');
    }

    this._slides[prev].classList.remove('is-active');
    if (this._transition === 'slide') {
      this._slides[prev].classList.add('is-prev');
    }

    setTimeout(() => {
      if (this._transition === 'slide') {
        this._slides[prev].style.transform = '';
        this._slides[next].style.transform = '';
        this._slides[prev].classList.remove('is-prev');
      }
      this._isAnimating = false;
    }, speed);

    this._currentIndex = next;
    this._updateUI();
    this._resetTimer();
  }

  /* ---- UI update ---- */
  _updateUI() {
    const i = this._currentIndex;

    /* Dots */
    this._dots.forEach((dot, idx) => {
      const active = idx === i;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });

    /* Counter text */
    if (this._counter) this._counter.textContent = i + 1;

    /* Counter bar */
    if (this._counterBar) {
      this._counterBar.style.width = `${((i + 1) / this._total) * 100}%`;
    }

    /* Progress fill restart */
    if (this._progressBar) {
      const dur = this._delay / 1000;
      this._progressBar.style.animation = 'none';
      void this._progressBar.offsetWidth; /* reflow */
      this._progressBar.style.animationDuration = `${dur}s`;
      this._progressBar.style.animation = `hero-progress ${dur}s linear forwards`;
    }

    /* Announce to screen readers */
    this.setAttribute('aria-label', `Slide ${i + 1} de ${this._total}`);
  }

  /* ---- Autoplay timer ---- */
  _startTimer() {
    if (!this._autoplay) return;
    this._clearTimer();
    this._timer = setInterval(() => {
      this._goTo(this._currentIndex + 1);
    }, this._delay);

    /* Restart progress bar */
    if (this._progressBar) {
      const dur = this._delay / 1000;
      this._progressBar.style.animation = 'none';
      void this._progressBar.offsetWidth;
      this._progressBar.style.animationDuration = `${dur}s`;
      this._progressBar.style.animation = `hero-progress ${dur}s linear forwards`;
    }
  }

  _clearTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    if (this._progressBar) {
      this._progressBar.style.animationPlayState = 'paused';
    }
  }

  _resetTimer() {
    if (this._autoplay) this._startTimer();
  }
}

/* ---- Register Custom Element ---- */
if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}

/* ============================================================
   Shopify Theme Editor support — re-init on section changes
   ============================================================ */
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('hero-slideshow');
  if (section) section._init();
});

document.addEventListener('shopify:block:select', (e) => {
  const slide  = e.target;
  const parent = slide.closest('hero-slideshow');
  if (!parent) return;
  const index  = parseInt(slide.dataset.slideIndex, 10);
  if (!isNaN(index)) {
    parent._clearTimer();
    parent._goTo(index);
  }
});

document.addEventListener('shopify:block:deselect', (e) => {
  const parent = e.target.closest('hero-slideshow');
  if (parent && parent._autoplay) parent._startTimer();
});

document.addEventListener('shopify:section:unload', (e) => {
  const section = e.target.querySelector('hero-slideshow');
  if (section) section.disconnectedCallback();
});